import type { GridPageFilters } from '../../../types/grid';
import { useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';
import type { GridApi } from 'ag-grid-community';
import { getFieldDependentTable } from '../../../utils/metadata';
import {
  adjustOrderByForRelationships,
  buildGridQueryConditions,
  type FilterAdjustmentParams,
} from './gridDataSource';
import type { CRMFilterCondition, CRMFilterModel, GridApiRef } from '../../../types/grid';
import type { QueryConditions } from '../../../types/supabase';
import type { DynamicRecord } from '../../../types/api';
import type { LiveChangeEventDetail } from '../../../utils/realtime/liveChanges';

/**
 * Live grid updates: applies changes made by other users to an already rendered grid.
 *
 * The grid is server-side paged, filtered and sorted, so a naive "insert the row at the top"
 * produces rows that do not belong in the current view and a row count that drifts. Instead:
 *
 *  - INSERT  -> debounced visible-page revalidation so the server decides whether (and where)
 *               the rows belong under the active filters, sort and page.
 *  - UPDATE  -> the changed rows that are loaded in this grid are re-read in one request
 *               *through the grid's own filters* and patched in place. A row that no longer
 *               matches, moved in the sort order, or is not loaded at all (it may now qualify)
 *               schedules a page revalidation instead.
 *  - DELETE  -> the visible page is revalidated so row count and block boundaries stay correct.
 *
 * Events arrive per statement with the changed ids (utils/realtime/liveChanges), so a bulk
 * write costs each tab one request rather than one per row. Refreshes are debounced, spaced
 * at least REFRESH_MIN_INTERVAL_MS apart, and deferred while the user is editing a cell or
 * the tab is hidden.
 */

const REFRESH_DEBOUNCE_MS = 600;
const REFRESH_MIN_INTERVAL_MS = 2000;

interface UseLiveGridUpdatesParams {
  tableName: string;
  schemaName: string;
  source?: 'view' | 'deepView' | 'base';
  gridApiRef: MutableRefObject<GridApiRef | null>;
  topLevelFilters?: GridPageFilters;
  searchFilterRef?: MutableRefObject<CRMFilterCondition | null>;
  searchConditionsRef?: MutableRefObject<QueryConditions | null>;
  searchColumn?: string;
  searchColumns?: readonly string[];
  metadata: FilterAdjustmentParams['metadata'];
  clientSideRowsRef?: MutableRefObject<DynamicRecord[]>;
  /** Disabled for controlled grids, which own their data. */
  enabled?: boolean;
}

const getSortedColumnIds = (api: GridApi): string[] =>
  api
    .getColumnState()
    .filter((column) => column.sort !== null && column.sort !== undefined)
    .map((column) => column.colId);

const areRowValuesEqual = (left: unknown, right: unknown): boolean => {
  if (Object.is(left, right)) return true;
  if (left === null || right === null || typeof left !== 'object' || typeof right !== 'object') {
    return false;
  }

  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) return false;
    return left.every((value, index) => areRowValuesEqual(value, right[index]));
  }

  const leftRecord = left as Record<string, unknown>;
  const rightRecord = right as Record<string, unknown>;
  const leftKeys = Object.keys(leftRecord);
  const rightKeys = Object.keys(rightRecord);
  if (leftKeys.length !== rightKeys.length) return false;

  return leftKeys.every(
    (key) => Object.prototype.hasOwnProperty.call(rightRecord, key)
      && areRowValuesEqual(leftRecord[key], rightRecord[key])
  );
};

export const useLiveGridUpdates = ({
  tableName,
  schemaName,
  source,
  gridApiRef,
  topLevelFilters,
  searchFilterRef,
  searchConditionsRef,
  searchColumn,
  searchColumns,
  metadata,
  clientSideRowsRef,
  enabled = true
}: UseLiveGridUpdatesParams): void => {
  // Kept in refs so the realtime listeners never need re-binding when filters change.
  const topLevelFiltersRef = useRef(topLevelFilters);
  topLevelFiltersRef.current = topLevelFilters;
  const metadataRef = useRef(metadata);
  metadataRef.current = metadata;

  useEffect(() => {
    if (!enabled || !tableName) return;

    let refreshTimer: ReturnType<typeof setTimeout> | null = null;
    let refreshPending = false;
    let refreshInFlight = false;
    let lastRefreshAt = 0;
    let cancelled = false;

    const currentConditions = (api: GridApi): QueryConditions =>
      buildGridQueryConditions({
        filterModel: api.getFilterModel() as CRMFilterModel,
        topLevelFilters: topLevelFiltersRef.current,
        searchFilter: searchFilterRef?.current ?? null,
        searchConditions: searchConditionsRef?.current ?? null,
        searchColumn,
        searchColumns,
        metadata: metadataRef.current,
        tableName
      });

    const getPageFingerprint = (api: GridApi): string => JSON.stringify({
      page: api.paginationGetCurrentPage(),
      pageSize: api.paginationGetPageSize(),
      filters: api.getFilterModel(),
      sort: api.getColumnState()
        .filter((column) => column.sort !== null && column.sort !== undefined)
        .map((column) => ({ colId: column.colId, sort: column.sort, sortIndex: column.sortIndex })),
      topLevelFilters: topLevelFiltersRef.current,
      searchFilter: searchFilterRef?.current ?? null,
      searchConditions: searchConditionsRef?.current ?? null,
    });

    const scheduleRefresh = (): void => {
      refreshPending = true;
      if (refreshTimer !== null) clearTimeout(refreshTimer);
      const delay = Math.max(REFRESH_DEBOUNCE_MS, lastRefreshAt + REFRESH_MIN_INTERVAL_MS - Date.now());
      refreshTimer = setTimeout(() => void runRefresh(), delay);
    };

    const runRefresh = async (): Promise<void> => {
      refreshTimer = null;
      const api = gridApiRef.current?.api;
      if (cancelled || !api) return;

      // Never reconcile data under an open editor or while the tab is hidden.
      if (document.visibilityState !== 'visible') {
        refreshPending = true;
        return;
      }
      if (api.getEditingCells().length > 0) {
        refreshPending = true;
        refreshTimer = setTimeout(() => void runRefresh(), REFRESH_DEBOUNCE_MS);
        return;
      }
      if (refreshInFlight) {
        refreshPending = true;
        return;
      }

      refreshPending = false;
      refreshInFlight = true;
      lastRefreshAt = Date.now();
      const fingerprint = getPageFingerprint(api);

      try {
        const pageSize = api.paginationGetPageSize();
        const startRow = api.paginationGetCurrentPage() * pageSize;
        const conditions = currentConditions(api);
        const sortedColumn = api.getColumnState()
          .filter((column) => column.sort !== null && column.sort !== undefined)
          .sort((left, right) => (left.sortIndex ?? 0) - (right.sortIndex ?? 0))[0];
        const orderBy = adjustOrderByForRelationships({
          column: sortedColumn?.colId ?? 'updated_at',
          direction: sortedColumn?.sort === 'asc' ? 'ascending' : 'descending',
        }, {
          metadata: metadataRef.current,
          tableName,
        });

        const { data, error, count } = await getFieldDependentTable({
          tableName,
          schema: schemaName,
          conditions,
          orderBy,
          limitOffset: { limit: Math.max(0, pageSize - 1), offset: startRow },
          withCount: true,
          noCache: true,
          source,
        });

        if (cancelled) return;
        const currentApi = gridApiRef.current?.api;
        if (!currentApi || currentApi !== api) return;
        if (fingerprint !== getPageFingerprint(api)) {
          refreshPending = true;
          return;
        }
        if (error) {
          console.error('[liveGrid] Error revalidating visible rows:', error);
          return;
        }

        const rows = Array.isArray(data) ? data : data ? [data] : [];
        const clientRows = startRow === 0 ? (clientSideRowsRef?.current ?? []) : [];
        const nextRows = [...clientRows, ...rows];
        const existingNodes = nextRows.map((_, index) => api.getDisplayedRowAtIndex(startRow + index));
        const sameRowsAtSameIndexes = existingNodes.every((node, index) =>
          node?.data?.id != null && String(node.data.id) === String(nextRows[index]?.id)
        );
        const totalRowCount = count == null
          ? undefined
          : count + (clientSideRowsRef?.current.length ?? 0);

        if (sameRowsAtSameIndexes) {
          // Most inserts do not affect the current filter/page, and most updates keep
          // their sort position. Refreshing the SSRM store anyway destroys every row
          // component. Patch matching nodes and update only the count so a no-op
          // background revalidation cannot blink the table.
          existingNodes.forEach((node, index) => {
            if (node && !areRowValuesEqual(node.data, nextRows[index])) {
              node.updateData(nextRows[index]);
            }
          });
          if (totalRowCount !== undefined && api.paginationGetRowCount() !== totalRowCount) {
            api.setRowCount(totalRowCount);
          }
          return;
        }

        api.applyServerSideRowData({
          startRow,
          successParams: {
            rowData: nextRows,
            rowCount: totalRowCount,
          },
        });
      } catch (error) {
        console.error('[liveGrid] Error revalidating visible rows:', error);
      } finally {
        refreshInFlight = false;
        if (!cancelled && refreshPending && refreshTimer === null) scheduleRefresh();
      }
    };

    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible' && refreshPending) {
        scheduleRefresh();
      }
    };

    const handleInsert = (event: Event): void => {
      const detail = (event as CustomEvent<LiveChangeEventDetail>).detail;
      if (!detail || detail.schema !== schemaName) return;
      scheduleRefresh();
    };

    const handleUpdate = async (event: Event): Promise<void> => {
      const detail = (event as CustomEvent<LiveChangeEventDetail>).detail;
      if (!detail || detail.schema !== schemaName) return;

      const api = gridApiRef.current?.api;
      if (!api) return;

      // Too many rows changed to list: revalidate the page.
      if (detail.ids === null) {
        scheduleRefresh();
        return;
      }

      const loadedNodes = detail.ids
        .map((id) => api.getRowNode(String(id)))
        .filter((node): node is NonNullable<typeof node> => Boolean(node));

      // Rows that are not loaded may now qualify for this view, let the server decide.
      if (loadedNodes.length < detail.ids.length) scheduleRefresh();
      if (loadedNodes.length === 0) return;

      const conditions = currentConditions(api);
      conditions['id'] = {
        filterType: 'number',
        type: 'in',
        colId: 'id',
        values: loadedNodes.map((node) => node.data?.id)
      };

      const { data, error } = await getFieldDependentTable({
        tableName,
        schema: schemaName,
        conditions,
        limitOffset: { limit: loadedNodes.length, offset: 0 },
        withCount: false,
        noCache: true,
        source
      });

      if (cancelled) return;

      if (error) {
        console.error('[liveGrid] Error fetching updated rows:', error);
        return;
      }

      const rows = (Array.isArray(data) ? data : data ? [data] : []) as DynamicRecord[];
      const rowsById = new Map(rows.map((row) => [String(row.id), row]));
      const sortedColumnIds = getSortedColumnIds(api);
      let needsRefresh = false;

      loadedNodes.forEach((rowNode) => {
        const updatedRow = rowsById.get(String(rowNode.data?.id));
        if (!updatedRow) {
          // The row no longer satisfies this grid's filters.
          needsRefresh = true;
          return;
        }

        // The row is still in the grid but belongs somewhere else in the sort order.
        if (sortedColumnIds.some((colId) => rowNode.data?.[colId] !== updatedRow[colId])) {
          needsRefresh = true;
        }

        // Updating the existing node preserves its row component and edit/selection state.
        // SSRM store transactions can reject partial stores, while updateData is deterministic
        // for a row that is already rendered and only refreshes cells whose values changed.
        if (!areRowValuesEqual(rowNode.data, updatedRow)) {
          rowNode.updateData(updatedRow);
        }
      });

      if (needsRefresh) scheduleRefresh();
    };

    const handleDelete = (event: Event): void => {
      const detail = (event as CustomEvent<LiveChangeEventDetail>).detail;
      if (!detail || detail.schema !== schemaName) return;

      // Re-read the visible page so row count and block boundaries stay authoritative.
      // Replacing row data at the existing indexes keeps the rendered store continuously
      // populated; refreshServerSide() briefly destroys every row component.
      scheduleRefresh();
    };

    const insertEventName = `${tableName}_insert`;
    const updateEventName = `${tableName}_update`;
    const deleteEventName = `${tableName}_delete`;

    globalThis.addEventListener(insertEventName, handleInsert);
    globalThis.addEventListener(updateEventName, handleUpdate);
    globalThis.addEventListener(deleteEventName, handleDelete);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      if (refreshTimer !== null) clearTimeout(refreshTimer);
      globalThis.removeEventListener(insertEventName, handleInsert);
      globalThis.removeEventListener(updateEventName, handleUpdate);
      globalThis.removeEventListener(deleteEventName, handleDelete);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, tableName, schemaName, source, gridApiRef, searchFilterRef, searchColumn, searchColumns, clientSideRowsRef]);
};
